import { useId } from 'react'

import classNames from 'classnames'

import { StringText } from '@/Atomic/Atoms/StringText'
import { Tooltip } from '@/Atomic/Molecules/Tooltip'
import './Toggle.scss'

export interface ToggleProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type'
> {
  /** Tooltip text shown when the toggle is checked (on). */
  tooltipOn?: string
  /** Tooltip text shown when the toggle is unchecked (off). */
  tooltipOff?: string
  label?: string
  'data-testid'?: string
}

export const Toggle: React.FC<ToggleProps> = ({
  className,
  disabled,
  checked,
  tooltipOn,
  tooltipOff,
  label,
  id,
  'data-testid': dataTestId,
  ...inputProps
}) => {
  const generatedId = useId()
  const inputId = id ?? (label ? generatedId : undefined)
  const tooltipId = useId()
  const hasTooltip = tooltipOn !== undefined || tooltipOff !== undefined
  const tooltipContent = checked ? tooltipOn : tooltipOff

  const toggle = (
    <label
      htmlFor={inputId}
      className={classNames('toggle', {
        'toggle--disabled': disabled,
        'toggle-labeled': !!label,
      })}
      data-testid={dataTestId}
      data-tooltip-id={hasTooltip ? tooltipId : undefined}
    >
      <input
        {...inputProps}
        id={inputId}
        checked={checked}
        type="checkbox"
        disabled={disabled}
        className={classNames('toggle__input', className)}
      />
      <span className="toggle__track">
        <span className="toggle__thumb" />
      </span>
      {label && <StringText value={label} variant="title" />}
    </label>
  )

  return (
    <>
      {toggle}
      {hasTooltip && <Tooltip id={tooltipId} content={tooltipContent} />}
    </>
  )
}
